import { createTheme } from '@mui/material/styles';

/**
 * SgeM Minimal High-Contrast Lime Theme
 * Based on UI/UX Pro Max Design Intelligence
 */
export const getTheme = (mode, primaryHex, secondaryHex) => {
    const isDark = mode === 'dark';

    // UI/UX Pro Max: Minimal High-Contrast Lime Palette
    const defaultPrimary = { main: '#f2b903', light: '#f2b903', dark: '#f2b903', contrastText: '#000000' };
    const defaultSecondary = { main: '#FFFFFF', light: '#FFFFFF', dark: '#f8fafc', contrastText: '#000000' };

    const isValidHex = (color) => typeof color === 'string' && color.startsWith('#');

    const primaryColor = isValidHex(primaryHex) ? { main: primaryHex, contrastText: '#000000' } : defaultPrimary;
    const secondaryColor = isValidHex(secondaryHex) ? { main: secondaryHex, contrastText: '#000000' } : defaultSecondary;

    return createTheme({
        palette: {
            mode,
            primary: primaryColor,
            secondary: secondaryColor,
            background: {
                default: isDark ? '#000000' : '#F8FAFC',
                paper: isDark ? '#0A0A0A' : '#FFFFFF',
            },
            text: {
                primary: isDark ? '#FFFFFF' : '#000000',
                secondary: isDark ? '#F8FAFC' : '#475569',
            },
            divider: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
            error: { main: '#FF4C4C' },
            warning: { main: '#FACC15' },
            info: { main: '#38BDF8' }
        },
        typography: {
            fontFamily: '"Fira Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
            h1: { fontWeight: 800, letterSpacing: '-0.04em' },
            h2: { fontWeight: 800, letterSpacing: '-0.04em' },
            h3: { fontWeight: 700, letterSpacing: '-0.03em' },
            h4: { fontWeight: 700, letterSpacing: '-0.02em' },
            h5: { fontWeight: 600 },
            h6: { fontWeight: 600 },
            button: { fontWeight: 600, textTransform: 'none', letterSpacing: '0.01em' },
            subtitle1: { fontWeight: 500 },
            subtitle2: { fontWeight: 500 },
            body1: { lineHeight: 1.6 },
            body2: { lineHeight: 1.6 },
        },
        shape: {
            borderRadius: 8, // enforce max 8px radius across components
        },
        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    body: {
                        backgroundColor: isDark ? '#000000' : '#F8FAFC',
                        minHeight: '100vh',
                        margin: 0,
                        padding: 0,
                    },
                    '*': {
                        borderRadius: '8px !important',
                    },
                    '*::-webkit-scrollbar': {
                        width: '6px',
                        height: '6px',
                    },
                    '*::-webkit-scrollbar-track': {
                        background: 'transparent',
                    },
                    '*::-webkit-scrollbar-thumb': {
                        background: isDark ? '#333333' : '#cbd5e1',
                        borderRadius: '10px',
                    },
                },
            },
            MuiAppBar: {
                styleOverrides: {
                    root: {
                        backgroundColor: isDark ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.8)',
                        backdropFilter: 'blur(20px)',
                        WebkitBackdropFilter: 'blur(20px)',
                        color: isDark ? '#FFFFFF' : '#000000',
                        boxShadow: 'none',
                        borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                        backgroundImage: 'none',
                    },
                },
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        backgroundImage: 'none',
                        backgroundColor: isDark ? '#0A0A0A' : '#FFFFFF',
                        boxShadow: 'none',
                        border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)'}`,
                        borderRadius: 8,
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            borderColor: isDark ? '#f2b903' : 'rgba(0,0,0,0.2)',
                            boxShadow: isDark ? '0 20px 40px -10px rgba(190, 242, 100, 0.1)' : '0 20px 40px -10px rgba(0, 0, 0, 0.05)',
                        }
                    },
                },
            },
            MuiButton: {
                styleOverrides: {
                    root: {
                        borderRadius: 8,
                        padding: '10px 24px',
                        fontWeight: 700,
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                            transform: 'scale(1.02)',
                        },
                        '&:active': {
                            transform: 'scale(0.98)',
                        }
                    },
                    containedPrimary: {
                        backgroundColor: '#f2b903',
                        color: '#000000',
                        '&:hover': {
                            backgroundColor: '#f2b903',
                        }
                    },
                    outlined: {
                        borderWidth: '2px',
                        '&:hover': {
                            borderWidth: '2px',
                        }
                    }
                },
            },
            MuiTextField: {
                styleOverrides: {
                    root: {
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 8,
                            backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                            transition: 'all 0.2s ease-in-out',
                            '& fieldset': {
                                borderColor: 'transparent',
                            },
                            '&:hover fieldset': {
                                borderColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)',
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: '#f2b903',
                                borderWidth: '2px',
                            }
                        },
                    },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        backgroundImage: 'none',
                        borderRadius: 16,
                    },
                },
            },
            MuiTableCell: {
                styleOverrides: {
                    head: {
                        backgroundColor: isDark ? '#000000' : '#F8FAFC',
                        color: isDark ? '#f2b903' : '#000000',
                        fontWeight: 800,
                        textTransform: 'uppercase',
                        fontSize: '0.7rem',
                        letterSpacing: '0.1em',
                        borderBottom: `2px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`
                    },
                    body: {
                        fontFamily: '"Fira Code", monospace', // Use code for data
                        fontSize: '0.85rem',
                        borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`
                    }
                }
            },
            MuiToggleButton: {
                styleOverrides: {
                    root: {
                        borderRadius: 12,
                        border: '1px solid transparent',
                        '&.Mui-selected': {
                            backgroundColor: '#f2b903',
                            color: '#000000',
                            '&:hover': {
                                backgroundColor: '#f2b903',
                            }
                        }
                    }
                }
            }
        },
    });
};